import { useCallback, useRef, useSyncExternalStore } from 'react'

import type { TruncationDetectionOptions } from '../types'

/**
 * Detects if an element's content is truncated (overflow with ellipsis)
 *
 * Uses ResizeObserver with debouncing to efficiently detect when
 * scrollWidth > clientWidth (horizontal truncation)
 *
 * @example
 * const { ref, isTruncated } = useTruncationDetection<HTMLParagraphElement>()
 *
 * return (
 *   <p ref={ref} className="truncate w-[200px]">
 *     {isTruncated ? 'Text is cut off' : 'Text fits'}
 *   </p>
 * )
 */
export function useTruncationDetection<T extends HTMLElement>(
	options: TruncationDetectionOptions = {},
) {
	const { debounceMs = 100 } = options
	const elRef = useRef<T>(null)
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	// Debounce the ResizeObserver callback (not the derived value) so rapid
	// resize bursts collapse into one snapshot read instead of one per frame.
	const subscribe = useCallback(
		(callback: () => void) => {
			const element = elRef.current
			if (!element) return () => {}

			const debouncedCallback = () => {
				if (timeoutRef.current) {
					clearTimeout(timeoutRef.current)
				}
				timeoutRef.current = setTimeout(callback, debounceMs)
			}

			const observer = new ResizeObserver(debouncedCallback)
			observer.observe(element)

			return () => {
				observer.disconnect()
				if (timeoutRef.current) {
					clearTimeout(timeoutRef.current)
				}
			}
		},
		[debounceMs],
	)

	// Truncation is a render-time read of live layout (scrollWidth/clientWidth).
	const getSnapshot = useCallback(() => {
		const element = elRef.current
		if (!element) return false
		return element.scrollWidth > element.clientWidth
	}, [])

	// No layout exists during SSR, so assume not-truncated to keep server and
	// first client render in sync (avoids hydration mismatch).
	const getServerSnapshot = useCallback(() => false, [])

	// Check if the element is truncated
	const isTruncated = useSyncExternalStore(
		subscribe,
		getSnapshot,
		getServerSnapshot,
	)

	return { ref: elRef, isTruncated }
}
